import { cookieYesDomainsID, type CookieYesConsentModel } from './cookieyesIds'
import { oneTrustDomainsID } from './oneTrustIds'

export type ConsentProviderName = 'cookieyes' | 'onetrust'

export interface OneTrustProvider {
  provider: 'onetrust'
  id: string
}

export interface CookieYesProvider {
  provider: 'cookieyes'
  id: string
  consentModel: CookieYesConsentModel
  migrateFromOneTrust: boolean
  bannerCustomizations: boolean
}

export type ConsentProvider = OneTrustProvider | CookieYesProvider

/** CookieYes site anahtarı: 24 (ObjectId) veya 32 karakterlik hex. */
export const isValidCookieYesId = (id?: string | null): id is string => typeof id === 'string' && /^[a-f0-9]{24,32}$/i.test(id)

/**
 * Host'a göre kullanılacak cookie consent sağlayıcısını çözer.
 *
 * Öncelik CookieYes'tedir: host için geçerli bir CookieYes id tanımlıysa OneTrust
 * yüklenmez. Aksi halde OneTrust id'si varsa OneTrust; hiçbiri yoksa `null`
 * (CookieAlert toast'u devreye girer).
 *
 * `host` pageProps'tan gelen tam URL (`https://www.enuygun.com`) veya hostname olabilir.
 */
export const resolveConsentProvider = (host?: string | null): ConsentProvider | null => {
  if (!host) return null

  const cookieYes = cookieYesDomainsID.find((item) => host.includes(item.host))
  if (cookieYes && isValidCookieYesId(cookieYes.id)) {
    return {
      provider: 'cookieyes',
      id: cookieYes.id,
      consentModel: cookieYes.consentModel,
      migrateFromOneTrust: cookieYes.migrateFromOneTrust,
      bannerCustomizations: cookieYes.bannerCustomizations,
    }
  }

  const oneTrust = oneTrustDomainsID.find((item) => host.includes(item.host))
  if (oneTrust?.id) {
    return { provider: 'onetrust', id: oneTrust.id }
  }

  return null
}

export const getCookieYesProvider = (provider: ConsentProvider | null): CookieYesProvider | null => (provider?.provider === 'cookieyes' ? provider : null)

/** KVKK örtülü onay modeli (sayfa geçişinde kabul + granted default) bu host'ta aktif mi? */
export const isImpliedConsentCookieYes = (provider: ConsentProvider | null): boolean => provider?.provider === 'cookieyes' && provider.consentModel === 'implied'
